import React from "react";
import ErrorBoundary from "./ErrorBoundary";
import Layout from "./Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowLeft, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PageErrorFallbackProps {
  pageName?: string;
}

const PageErrorFallback: React.FC<PageErrorFallbackProps> = ({ pageName = "This page" }) => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <CardTitle className="text-2xl">{pageName} is temporarily unavailable</CardTitle>
            <CardDescription className="text-base mt-2">
              We're having trouble loading this content. This is usually temporary.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-medium text-sm mb-2">What you can try:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Refresh the page to try loading it again</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Check your internet connection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Clear your browser cache and cookies</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Try again in a few minutes</span>
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button onClick={() => window.location.reload()} size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </Button>
              <Button onClick={() => navigate(-1)} variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                If this issue continues, please contact support or try using a different browser.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

interface PageErrorBoundaryProps {
  children: React.ReactNode;
  pageName?: string;
}

export const PageErrorBoundary: React.FC<PageErrorBoundaryProps> = ({ children, pageName }) => {
  return (
    <ErrorBoundary fallback={<PageErrorFallback pageName={pageName} />}>
      {children}
    </ErrorBoundary>
  );
};

export default PageErrorBoundary;